<script lang="ts" setup>
import { InvoiceGroup } from '@/core/collections/contracts-api/companies/invoice-groups/invoice-group.interface';
import { Shift } from '@/core/collections/contracts-api/companies/shifts/shift.interface';
import { filterEligibleFulltimes } from '@/core/collections/contracts-api/company-joint-committees/fulltimes/filter-eligible-fulltimes.helper';
import { Fulltime } from '@/core/collections/contracts-api/company-joint-committees/fulltimes/fulltime.interface';
import {
   ContractTemplateFieldName,
   ContractTemplateFieldNameKey,
} from '@/core/collections/contracts-api/contract-template-field-names/contract-template-field-name.interface';
import { ContractTemplateField } from '@/core/collections/contracts-api/customers/contract-templates/contract-template.interface';
import { Customer } from '@/core/collections/contracts-api/customers/customer.interface';
import { JointCommittee } from '@/core/collections/contracts-api/joint-committees/joint-committee.interface';
import { WorkerStatute } from '@/core/collections/contracts-api/worker-statutes/worker-statute.interface';
import { WorkerType } from '@/core/collections/contracts-api/worker-types/worker-type.interface';
import Dropdown from '@/core/components/form-elements/Dropdown.vue';
import JointCommitteeDropdown from '@/core/components/form-elements/JointCommitteeDropdown.vue';
import { useStore } from '@/core/hooks/useStore';
import { FormType } from '@/core/models/form-type.enum';
import FulltimeDropdown from '@/modules/contract/components/employment-wizard/basic-info/FulltimeDropdown.vue';
import { NxtFormSection, NxtInput } from '@nxtpeople/nxt-styles';
import { Team } from '@nxtpeople/nxt-vue-library';
import { PropType, computed, onMounted, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ContractTemplateWizardField from './ContractTemplateWizardField.vue';
import { useContractTemplate } from './useContractTemplate';
import { useContractTemplateKeys } from './useContractTemplateKeys';

const { t } = useI18n();
const { state, dispatch } = useStore();
const contractTemplate = useContractTemplate();
const { WHEN_FIELD_KEYS, WHO_WHAT_WHERE_FIELD_KEYS } = useContractTemplateKeys();

//#region Props
const emits = defineEmits<{
   (e: 'onUpdateField', payload: { fieldNameId: number; key: string; value: any }): void;
   (e: 'onInitialized'): void;
}>();
const props = defineProps({
   fields: { type: Array as PropType<Array<ContractTemplateField>>, default: () => [] },
   fieldNames: {
      type: Array as PropType<Array<ContractTemplateFieldName>>,
      default: () => [],
   },
   loading: { type: Boolean, default: false },
   errors: { type: Object, default: () => ({}) },
});

const { fields, fieldNames } = toRefs(props);
//#endregion

//#region JointCommittees
async function getCompanyJointCommittees(companyId: number): Promise<Array<any>> {
   const jointCommittees = await dispatch('company/jointCommittee/getJointCommittees', companyId);
   return jointCommittees.map((jc: any) => ({ ...jc, ...jc.joint_committee }));
}
//#endregion

//#region Employer
const employerOptions = computed(() => state.employer?.employerOptions || []);
const employerJointCommittees = ref<Array<any>>([]);
const employerCompanyId = computed(() => contractTemplate.value?.employer?.company_id);

onMounted(async () => {
   if (contractTemplate.value?.employer_company_joint_committee?.joint_committee) {
      employerJointCommittees.value.push({
         ...contractTemplate.value.employer_company_joint_committee.joint_committee,
         id: contractTemplate.value.employer_company_joint_committee_id,
      });
   }

   if (contractTemplate.value?.user_company_joint_committee?.joint_committee) {
      userJointCommittees.value.push({
         ...contractTemplate.value.user_company_joint_committee.joint_committee,
         id: contractTemplate.value.user_company_joint_committee_id,
      });
   }

   await Promise.all([getUserJointCommittees(customerCompanyId.value), getEmployerJointCommittees(employerCompanyId.value)]);

   emits('onInitialized');
});

watch(employerCompanyId, getEmployerJointCommittees);

async function getEmployerJointCommittees(employerCompanyId?: number) {
   if (!employerCompanyId) return;

   employerJointCommittees.value = await getCompanyJointCommittees(employerCompanyId); // fetches al possible options
   if (
      employerJointCommittees.value.length === 1 &&
      employerJointCommittees.value[0].id !== contractTemplate.value.employer_company_joint_committee_id
   ) {
      contractTemplate.value.employer_company_joint_committee_id = employerJointCommittees.value[0].id;
      contractTemplate.value.employer_company_joint_committee = {
         company: employerOptions.value.find((eo) => eo.id === contractTemplate.value.employer_id)?.company,
         joint_committee: userJointCommittees.value[0],
      };
   }
}

function setEmployerJointCommittee(joint_committee?: JointCommittee) {
   contractTemplate.value.employer_company_joint_committee_id = joint_committee?.id;
   contractTemplate.value.employer_company_joint_committee = joint_committee
      ? {
           company: employerOptions.value.find((eo) => eo.company_id === employerCompanyId.value)?.company,
           joint_committee,
        }
      : undefined;
}
//#endregion

//#region User
const customerCompanyId = computed(() => contractTemplate.value.customer?.company_id);
const userJointCommittees = ref<Array<any>>([]);

watch(customerCompanyId, getUserJointCommittees, { immediate: true });

async function getUserJointCommittees(customerCompanyId?: number) {
   if (!customerCompanyId) return;

   userJointCommittees.value = await getCompanyJointCommittees(customerCompanyId);

   if (userJointCommittees.value.length === 1) {
      if (contractTemplate.value.user_company_joint_committee_id === userJointCommittees.value[0].id) return;

      contractTemplate.value.user_company_joint_committee_id = userJointCommittees.value[0].id;
      contractTemplate.value.user_company_joint_committee = {
         company: contractTemplate.value.customer?.company,
         joint_committee: userJointCommittees.value[0],
      };
   }
}

function setUserJointCommittee(joint_committee?: JointCommittee) {
   contractTemplate.value.user_company_joint_committee_id = joint_committee?.id;
   contractTemplate.value.user_company_joint_committee = joint_committee
      ? {
           company: contractTemplate.value.customer?.company,
           joint_committee,
        }
      : undefined;
}
//#endregion

//#region Color
const colorFieldName = computed(() => fieldNames.value?.find((fieldName) => fieldName.key === ContractTemplateFieldNameKey.Color));
//#endregion

//#region Worker Statutes
const workerStatutes = ref<Array<WorkerStatute>>([]);
const jointCommitteeNumber = computed(() => contractTemplate.value.user_company_joint_committee?.joint_committee?.number);
const workerStatuteId = computed(
   () =>
      contractTemplate.value.contract_template_fields.find((ctf) => ctf.field_key === ContractTemplateFieldNameKey.WorkerStatuteId)?.value,
);
watch(jointCommitteeNumber, getWorkerStatutes, { immediate: true });
async function getWorkerStatutes(joint_committee_number?: string) {
   if (!joint_committee_number) return (workerStatutes.value = []);

   workerStatutes.value = await dispatch('workerStatute/getFilteredWorkerStatutes', {
      joint_committee_number,
   });
}

watch(workerStatutes, checkValidWorkerStatute);
function checkValidWorkerStatute(workerStatues: Array<WorkerStatute>) {
   const valid = workerStatues.find((ws) => ws.id === workerStatuteId.value);
   if (valid) return;
   else {
      const index = contractTemplate.value.contract_template_fields.findIndex(
         (ctf) => ctf.field_key === ContractTemplateFieldNameKey.WorkerStatuteId,
      );
      if (index > -1) delete contractTemplate.value.contract_template_fields[index].value;
   }
}
//#endregion

//#region Worker Types
const workerTypes = ref<Array<WorkerType>>([]);
const workerTypeId = computed(
   () => contractTemplate.value.contract_template_fields.find((ctf) => ctf.field_key === ContractTemplateFieldNameKey.WorkerTypeId)?.value,
);

const customerId = computed(() => contractTemplate.value.customer_id);

watch([jointCommitteeNumber, workerStatuteId, customerId], getWorkerTypes, { immediate: true });
async function getWorkerTypes(payload: [string | undefined, number | undefined, number | undefined]) {
   const [joint_committee_number, worker_statute_id, customer_id] = payload;

   if (!joint_committee_number || !worker_statute_id) return (workerTypes.value = []);

   workerTypes.value = await dispatch('workerType/getFilteredWorkerTypes', { joint_committee_number, worker_statute_id, customer_id });
}

watch(workerTypes, checkValidWorkerType);
function checkValidWorkerType(workerTypes: Array<WorkerType>) {
   const valid = workerTypes.find((wt) => wt.id === workerTypeId.value);
   if (valid) return;
   else {
      const index = contractTemplate.value.contract_template_fields.findIndex(
         (ctf) => ctf.field_key === ContractTemplateFieldNameKey.WorkerTypeId,
      );
      if (index > -1) delete contractTemplate.value.contract_template_fields[index].value;
   }
}
//#endregion

//#region Fulltimes
const userCompanyJointCommitteeId = computed(() => contractTemplate.value.user_company_joint_committee_id);
const fulltimeFieldName = computed(() =>
   fieldNames.value.find((fieldName) => fieldName.key === ContractTemplateFieldNameKey.FulltimeWeekId),
);

const fulltimeId = computed(
   () =>
      contractTemplate.value.contract_template_fields.find((ctf) => ctf.field_key === ContractTemplateFieldNameKey.FulltimeWeekId)?.value,
);

const eligibleFulltimes = computed(() =>
   filterEligibleFulltimes(fulltimeFieldName.value?.options || [], {
      companyJointCommitteeId: userCompanyJointCommitteeId.value,
      workerStatuteId: workerStatuteId.value,
      workerTypeId: workerTypeId.value,
   }),
);

watch(eligibleFulltimes, checkValidFulltime, { immediate: true });

function checkValidFulltime(fulltimes: Array<Fulltime>) {
   if (fulltimeId.value == null || fulltimeId.value === '') return;
   if (!(fulltimeFieldName.value?.options || []).length) return;

   const valid = fulltimes.some((fulltime) => (fulltime.weeks || []).some((week) => week.id == fulltimeId.value));
   if (valid) return;

   const index = contractTemplate.value.contract_template_fields.findIndex(
      (ctf) => ctf.field_key === ContractTemplateFieldNameKey.FulltimeWeekId,
   );
   if (index > -1) delete contractTemplate.value.contract_template_fields[index].value;
}
//#endregion

//#region Shifts
const customer = inject('customer', ref<Customer>());
const company = computed(() => customer.value?.company);
const shifts = ref<Array<Shift>>([]);
const shiftId = computed(
   () => contractTemplate.value.contract_template_fields.find((ctf) => ctf.field_key === ContractTemplateFieldNameKey.ShiftId)?.value,
);
const shift = computed(() => shifts.value.find((s) => s.id === shiftId.value));

function filterShifts(fulltimeId: number) {
   shifts.value = company.value?.shifts?.filter((s) => s.fulltime_week_id === fulltimeId) || [];
}
function validateShift(shift?: Shift) {
   if (shift?.fulltime_week_id === fulltimeId.value) return;
   else {
      const index = contractTemplate.value.contract_template_fields.findIndex(
         (ctf) => ctf.field_key === ContractTemplateFieldNameKey.ShiftId,
      );
      if (index > -1) delete contractTemplate.value.contract_template_fields[index].value;
   }
}

watch(fulltimeId, filterShifts, { immediate: true });
watch(shift, validateShift);
//#endregion

//#region Fields
const fulltimeField = computed(() => {
   const field = contractTemplate.value.contract_template_fields.find(
      (ctf) => ctf.field_key === ContractTemplateFieldNameKey.FulltimeWeekId,
   );

   return field ? { ...field, options: eligibleFulltimes.value } : undefined;
});
const teams = computed(() => {
   const teamId = contractTemplate.value.contract_template_fields.find(
      (ctf) => ctf.field_key === ContractTemplateFieldNameKey.TeamId,
   )?.value;

   if (!teamId) return state.userInfo?.userInfo?.teams ?? [];

   if (state.userInfo?.userInfo?.teams?.find((t: Team) => t.id === teamId)) {
      return state.userInfo?.userInfo?.teams ?? [];
   }

   return undefined;
});

const whoWhatWhereFieldsNames = computed(() =>
   fieldNames.value
      .filter((fn) => WHO_WHAT_WHERE_FIELD_KEYS.value.find(({ key }) => fn.key === key))
      .map((fieldName) => {
         if (fieldName.key === ContractTemplateFieldNameKey.WorkerStatuteId) {
            return { ...fieldName, options: workerStatutes.value };
         }
         if (fieldName.key === ContractTemplateFieldNameKey.WorkerTypeId) {
            return { ...fieldName, options: workerTypes.value };
         }
         if (fieldName.key === ContractTemplateFieldNameKey.TeamId) {
            return {
               ...fieldName,
               options: teams.value,
            };
         }

         return { ...fieldName };
      }),
);

const whenFieldNames = computed(() =>
   fieldNames.value
      .filter((fn) => WHEN_FIELD_KEYS.value.find(({ key }) => fn.key === key))
      .map((fieldName) => {
         if (fieldName.key === ContractTemplateFieldNameKey.FulltimeWeekId) {
            return { ...fieldName, options: eligibleFulltimes.value };
         }

         if (fieldName.key === ContractTemplateFieldNameKey.ShiftId) {
            return { ...fieldName, options: shifts.value };
         }

         return { ...fieldName };
      }),
);

function getField(fieldNameId: number) {
   return fields.value.find((field) => field.contract_template_field_name_id === fieldNameId);
}
//#endregion

//#region Invoice Groups
const invoiceGroupFieldName = computed(() =>
   fieldNames.value.find((fieldName) => fieldName.key === ContractTemplateFieldNameKey.InvoiceGroupId),
);

const invoiceGroups = computed(() => invoiceGroupFieldName.value?.options || []);
watch(invoiceGroups, autoSelectInvoiceGroup);
function autoSelectInvoiceGroup(invoiceGroups: Array<InvoiceGroup>) {
   if (invoiceGroups.length === 1) {
      emits('onUpdateField', {
         fieldNameId: invoiceGroupFieldName.value!.id,
         key: 'value',
         value: invoiceGroups[0].id,
      });
   }
}
//#endregion
</script>

<template>
   <div class="contract-template-wizard-basic-data">
      <!-- TEMPLATE NAME -->
      <NxtFormSection :title="t('generic.name')">
         <div class="contract-template-wizard-basic-data__section">
            <NxtInput
               v-model="contractTemplate.name"
               :name="'name'"
               :label="t('generic.name')"
               :errors="errors"
               :mode="'light'"
               :required="true"
               :disabled="loading"
            />
         </div>
      </NxtFormSection>

      <!-- COLOR -->
      <NxtFormSection :title="t('generic.color')">
         <ContractTemplateWizardField
            v-if="colorFieldName"
            :key="colorFieldName.key"
            :mode="'light'"
            :errors="errors"
            :disabled="loading"
            :field-name="{ ...colorFieldName, form_type: FormType.Color }"
            :field="getField(colorFieldName.id)"
            :hide-calculated-values="!colorFieldName.allows_conditions"
            @on-update-field="emits('onUpdateField', $event)"
         />
      </NxtFormSection>

      <!-- WHO WHAT WHERE -->
      <NxtFormSection :title="t('contracts.who-what-where')">
         <div class="contract-template-wizard-basic-data__section">
            <!-- Employer -->
            <Dropdown
               :model-value="contractTemplate.employer_id"
               :label="$t('contracts.employer', 1)"
               :name="'employer_id'"
               :mode="'light'"
               :disabled="loading"
               :required="true"
               :errors="errors"
               :display-key="`company.name`"
               :options="employerOptions"
               :emit-object="true"
               @update:model-value="
                  (employer) => {
                     contractTemplate.employer_id = employer?.id;
                     contractTemplate.employer = employer;
                  }
               "
            />
            <JointCommitteeDropdown
               :joint-committee-id="contractTemplate.employer_company_joint_committee_id"
               :label="$t('contracts.employer-joint-commitee')"
               :name="'employer_company_joint_committee_id'"
               :disabled="loading || !contractTemplate.employer_id"
               :required="true"
               :joint-committees="employerJointCommittees"
               :mode="'light'"
               :errors="errors"
               @update:joint-committee="setEmployerJointCommittee"
            />

            <!-- User -->
            <Dropdown
               :model-value="contractTemplate.customer_id"
               :label="$t('companies.company', 1)"
               :name="'user_id'"
               :disabled="true"
               :mode="'light'"
               :errors="errors"
               :options="[contractTemplate.customer]"
            />
            <JointCommitteeDropdown
               :joint-committee-id="contractTemplate.user_company_joint_committee_id"
               :label="$t('contracts.company-joint-commitee')"
               :name="'employer_company_joint_committee_id'"
               :disabled="
                  loading || !contractTemplate.customer_id || !!contractTemplate.default_commercial_agreement?.joint_committee_number
               "
               :mode="'light'"
               :required="true"
               :joint-committees="userJointCommittees"
               :errors="errors"
               @update:joint-committee="setUserJointCommittee"
            />

            <!-- Fields -->
            <ContractTemplateWizardField
               v-for="fieldName in whoWhatWhereFieldsNames"
               :key="fieldName.key"
               :errors="errors"
               :mode="'light'"
               :disabled="loading"
               :field-name="fieldName"
               :hide-calculated-values="!fieldName.allows_conditions"
               :field="getField(fieldName.id)"
               @on-update-field="emits('onUpdateField', $event)"
            />
         </div>
      </NxtFormSection>

      <!-- WHEN -->
      <NxtFormSection :title="t('generic.dates.when')">
         <div class="contract-template-wizard-basic-data__section">
            <!-- Fulltime Dropdown -->
            <FulltimeDropdown
               v-if="fulltimeFieldName"
               v-model="fulltimeId"
               :name="fulltimeFieldName.key"
               :mode="'light'"
               :label="fulltimeFieldName.label"
               :fulltimes="eligibleFulltimes"
               :disabled="loading"
               :errors="errors"
               :required="!!fulltimeFieldName.is_required"
               @update:model-value="emits('onUpdateField', { fieldNameId: fulltimeFieldName.id, key: 'value', value: $event })"
            />

            <!-- Other WHEN fields -->
            <ContractTemplateWizardField
               v-for="fieldName in whenFieldNames.filter((fn) => fn.key !== 'fulltime_week_id')"
               :key="fieldName.key"
               :errors="errors"
               :disabled="loading"
               :field-name="fieldName"
               :mode="'light'"
               :field="getField(fieldName.id)"
               :hide-calculated-values="!fieldName.allows_conditions"
               @on-update-field="emits('onUpdateField', $event)"
            />
         </div>
      </NxtFormSection>
   </div>
</template>

<style lang="scss" scoped>
.contract-template-wizard-basic-data {
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter);
}
.contract-template-wizard-basic-data__section {
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: var(--nxt-gutter);
   margin-right: toRem(250);
}
</style>
